import {
    CardElement,
    useElements,
    useStripe,
} from "@stripe/react-stripe-js";

import {
    Alert,
    Box,
    Card,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";

import { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { loadCards, useAppDispatch, useAppSelector, payments, selectCard, hideShowAddCards, setShowAddCards, deleteCard } from "@/redux";
import { PaymentsState } from "@/redux/reducers/paymentCards/paymentCards.types";
import { createSetupIntent } from "@/redux";
import Collapse from "@mui/material/Collapse";
import SavedCardsList from "./components/SavedCardsList";
import AddCardForm from "./components/AddCardForm";

export default function PaymentMethodSelector() {

    const stripe = useStripe();
    const elements = useElements();
    const auth = getAuth();
    const dispatch = useAppDispatch();
    const paymentState: PaymentsState = useAppSelector(payments);

    const [cardComplete, setCardComplete] = useState(false);

    // ============================
    // LOAD SAVED CARDS
    // ============================


    useEffect(() => {
        auth.currentUser?.getIdToken().then(token => dispatch(loadCards(token)))
    }, [dispatch, auth]);

    // ============================
    // SAVE NEW CARD, UNIDIRECTIONAL FLOW
    // ============================

    const handleAddCard = async () => {

        if (!stripe || !elements) return;

        const token = await auth.currentUser?.getIdToken() as string

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            return
        };

        const { clientSecret } = await createSetupIntent(token);

        await stripe.confirmCardSetup(
            clientSecret,
            {
                payment_method: {
                    card: cardElement,
                },
            }
        );

        dispatch(loadCards(token))

    };

    // ============================
    // LOADING
    // ============================

    if (paymentState.loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center"
                }}

            >
                <CircularProgress />
            </Box>
        );
    }

    // ============================
    // UI
    // ============================

    return (
        <Card
            sx={{
                p: 3,
                borderRadius: 3,
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    color: "text.primary",
                    letterSpacing: "0.08em",
                    mb: 1.5,
                    display: "block"
                }}
            >
                Choose your Payment Method
            </Typography>


            {/* ============================
          SAVED CARDS
      ============================ */}

            <Collapse
                in={
                    paymentState.cards.length > 0 &&
                    !paymentState.showAddCard
                }
                timeout={1000}
                unmountOnExit
            >


                <SavedCardsList
                    cards={paymentState.cards}
                    selectedCardId={paymentState.selectedCardId}
                    onSelectCard={(id) => dispatch(selectCard(id))}
                    onDeleteCard={async (deleteCardId) => {

                        const token = await auth?.currentUser?.getIdToken();
                        if (!token) return;
                        dispatch(deleteCard({
                            token,
                            id: deleteCardId
                        })
                        );
                    }}
                    addAnotherCard={() => dispatch(setShowAddCards())}
                />

            </Collapse>

            {/* ============================
          ADD CARD
      ============================ */}

            <Collapse
                in={paymentState.showAddCard}
                timeout={1000}
                unmountOnExit
            >

                {paymentState.showAddCard && (

                    <AddCardForm
                        cardComplete={cardComplete}
                        onCardComplete={setCardComplete}
                        onAddCard={handleAddCard}
                        onBack={() => dispatch(hideShowAddCards())}
                        showBackButton={paymentState.cards.length > 0}
                    />
                )}
            </Collapse>


            {paymentState.errors.length > 0 && <>
                <Stack spacing={1}>
                    {paymentState.errors.map((error, index) => (
                        <Alert
                            key={`${error}-${index}`}
                            severity="error"
                        >
                            {error}
                        </Alert>
                    ))}
                </Stack>
            </>
            }
        </Card>
    );
}